import { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Spinner } from '../components/ui/Spinner'

function friendlyError(msg = '') {
  if (msg.toLowerCase().includes('invalid login'))      return 'Email or password is incorrect.'
  if (msg.toLowerCase().includes('email not confirmed')) return 'Please confirm your email address first.'
  if (msg.toLowerCase().includes('rate limit'))          return 'Too many attempts — please wait a moment and try again.'
  return 'Sign-in failed. Please try again.'
}

export const LoginPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const from     = location.state?.from?.pathname ?? '/calendar'

  const [mode,      setMode]      = useState('login') // 'login' | 'reset'
  const [email,     setEmail]     = useState('')
  const [password,  setPassword]  = useState('')
  const [error,     setError]     = useState('')
  const [resetSent, setResetSent] = useState(false)
  const [loading,   setLoading]   = useState(false)

  const handleLogin = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    const { error: err } = await supabase.auth.signInWithPassword({ email, password })
    setLoading(false)
    if (err) { setError(friendlyError(err.message)); return }
    navigate(from, { replace: true })
  }

  const handleReset = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    const { error: err } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/settings?tab=account`,
    })
    setLoading(false)
    if (err) { setError(friendlyError(err.message)); return }
    setResetSent(true)
  }

  return (
    <div className="min-h-full flex flex-col items-center justify-center bg-background px-5 py-12">
      <div className="w-full max-w-sm">

        {/* Logo */}
        <div className="mb-8 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary mb-4 shadow-card">
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-text-primary">DoorChart</h1>
          <p className="text-sm text-text-muted mt-1">
            {mode === 'login' ? 'Sign in to your household' : 'Reset your password'}
          </p>
        </div>

        {/* ── Login form ─────────────────────────────────────────────── */}
        {mode === 'login' && (
          <form onSubmit={handleLogin} className="space-y-4">
            <Input label="Email" type="email" placeholder="you@example.com"
              value={email} onChange={(e) => setEmail(e.target.value)}
              autoComplete="email" required
            />
            <div className="space-y-1">
              <Input label="Password" type="password" placeholder="••••••••"
                value={password} onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password" required
              />
              <div className="text-right">
                <button type="button" onClick={() => { setMode('reset'); setError('') }}
                  className="text-xs text-text-muted hover:text-primary transition-colors">
                  Forgot password?
                </button>
              </div>
            </div>

            {error && (
              <div className="rounded-base bg-rose-50 border border-rose-200 px-4 py-3 text-sm text-rose-700">
                {error}
              </div>
            )}

            <Button type="submit" className="w-full" size="lg" disabled={loading}>
              {loading ? <><Spinner size="sm" /> Signing in…</> : 'Sign in'}
            </Button>
          </form>
        )}

        {/* ── Reset password form ────────────────────────────────────── */}
        {mode === 'reset' && (
          resetSent ? (
            <div className="text-center space-y-5">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-emerald-100 text-emerald-600">
                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-sm text-text-secondary">
                Check your inbox — we sent a reset link to <strong>{email}</strong>.
              </p>
              <Button variant="ghost" onClick={() => { setMode('login'); setResetSent(false) }}>
                ← Back to sign in
              </Button>
            </div>
          ) : (
            <form onSubmit={handleReset} className="space-y-4">
              <Input label="Your email address" type="email" placeholder="you@example.com"
                value={email} onChange={(e) => setEmail(e.target.value)}
                hint="We'll send a link to reset your password."
                autoComplete="email" required
              />

              {error && (
                <div className="rounded-base bg-rose-50 border border-rose-200 px-4 py-3 text-sm text-rose-700">
                  {error}
                </div>
              )}

              <Button type="submit" className="w-full" size="lg" disabled={loading}>
                {loading ? <><Spinner size="sm" /> Sending…</> : 'Send reset link'}
              </Button>
              <div className="text-center">
                <button type="button" onClick={() => { setMode('login'); setError('') }}
                  className="text-sm text-text-muted hover:text-primary transition-colors">
                  ← Back to sign in
                </button>
              </div>
            </form>
          )
        )}

        <p className="mt-8 text-center text-xs text-text-muted">
          DoorChart is invite-only. Ask your household admin to send you an invite link.
        </p>
      </div>
    </div>
  )
}
